import { useEffect, useState, useMemo } from "react";
import {
  X,
  ChevronRight,
  PanelTop,
  SlidersHorizontal,
  SquarePen,
  Settings,
  Bug,
  HelpCircle,
  type LucideIcon,
} from "lucide-react";
import { changelog, ChangelogItem, ChangelogCategory } from "@/shared/data/changelog";
import styles from "./ChangelogModal.module.css";

// Icon mapping - add new icons here as needed
const ICONS: Record<string, LucideIcon> = {
  "panel-top": PanelTop,
  "sliders-horizontal": SlidersHorizontal,
  "square-pen": SquarePen,
  settings: Settings,
  bug: Bug,
  "help-circle": HelpCircle,
};

// Tab configuration
const TABS: { id: ChangelogCategory; label: string }[] = [
  { id: "updates", label: "Updates" },
  { id: "bugfixes", label: "Bug Fixes" },
  { id: "support", label: "Support" },
];

const isVideo = (src: string) => /\.(mp4|webm|mov)$/i.test(src);

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ChangelogModal({ isOpen, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<ChangelogCategory>("updates");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Get latest entry and filter items by active tab
  const latestEntry = changelog[0];
  const items = useMemo(
    () => latestEntry?.items.filter((item) => item.category === activeTab) ?? [],
    [latestEntry, activeTab]
  );

  const formattedDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const renderCard = (item: ChangelogItem, index: number) => {
    const key = `${item.category}-${index}`;
    const isExpanded = !collapsed.has(key);
    const Icon = item.titleIcon ? ICONS[item.titleIcon] : null;

    return (
      <div key={key} className={styles.card}>
        <div className={styles.cardHeader}>
          {Icon && <Icon size={18} className={styles.cardIcon} />}
          <h4 className={styles.cardTitle}>{item.title}</h4>
        </div>

        <p className={styles.cardDesc}>{item.description}</p>

        {item.media && (
          <div className={styles.media}>
            {isVideo(item.media.src) ? (
              <video src={item.media.src} autoPlay muted loop playsInline />
            ) : (
              <img src={item.media.src} alt={item.media.alt} loading="lazy" />
            )}
          </div>
        )}

        {item.details && item.details.length > 0 && (
          <>
            <button
              onClick={() => toggleCollapse(key)}
              className={styles.toggle}
              aria-expanded={isExpanded}
            >
              <ChevronRight size={14} className={isExpanded ? styles.expanded : ""} />
              {isExpanded ? "Hide details" : "Show details"}
            </button>
            {isExpanded && (
              <ul className={styles.details}>
                {item.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>What's New</h2>
            <p className={styles.date}>{formattedDate}</p>
          </div>
          <button onClick={onClose} className={styles.close} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {items.length > 0 ? (
            items.map((item, i) => renderCard(item, i))
          ) : (
            <p className={styles.empty}>No items in this category yet.</p>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button onClick={onClose} className={styles.footerBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}
